import { useCallback, useMemo, useState } from "react";

import { useCopyToClipboard } from "@/lib/react-use/use-copy-to-clipboard";

import { FeatureIslandContainer, FeatureIslandHeader } from "@/components/features/_layouts";
import { QuestionForMain } from "@/components/features/_layouts/island/main/question";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { Button } from "@/components/ui/button/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/input/select";

import { SiteEmbedDescription } from "./site-embed-description";
import {
	getEmbedScriptSnippet,
	getMountTagSnippet,
	LANG_OPTIONS,
	SITE_EMBED_TAB_LIST,
	VARIANT_ICONS,
} from "./site-embed-settings-constants";
import { SiteEmbedSettingsPreview } from "./site-embed-settings-preview";

import type { LangType, SiteEmbedVariant } from "../site-embed-preview/types";
import type { ReactNode } from "react";



type Props = {
	hotelID: string;
};

const SectionCard = ({ children }: { children: ReactNode }) => {
	return <div className="rounded-xl border border-zinc-200 bg-white p-5">{children}</div>;
};

export const SiteEmbedSettings = ({ hotelID }: Props) => {
	const [variant, setVariant] = useState<SiteEmbedVariant>("button");
	const [lang, setLang] = useState<LangType>("default");

	const selectedTab = useMemo(() => {
		return SITE_EMBED_TAB_LIST.find((tab) => tab.type === variant) ?? SITE_EMBED_TAB_LIST[0];
	}, [variant]);

	const previewLangBadgeLabel = useMemo(() => {
		if (lang === "default") return "自動";
		return LANG_OPTIONS.find((o) => o.value === lang)?.label ?? lang;
	}, [lang]);

	const mountSnippet = useMemo<string>(() => getMountTagSnippet(variant), [variant]);
	const scriptSnippet = useMemo<string>(() => getEmbedScriptSnippet({ hotelID, lang }), [hotelID, lang]);

	const { copy } = useCopyToClipboard();
	const handleCopyMountSnippet = useCallback(() => {
		copy(mountSnippet);
		notifyUserEventInfo({
			title: "表示位置のタグをコピーしました",
		});
	}, [copy, mountSnippet]);
	const handleCopyScriptSnippet = useCallback(() => {
		copy(scriptSnippet);
		notifyUserEventInfo({
			title: "埋め込み用タグをコピーしました",
		});
	}, [copy, scriptSnippet]);

	const previewProps = {
		hotelID,
		variant,
		lang,
		previewLangBadgeLabel,
	};

	return (
		<FeatureIslandContainer>
			<FeatureIslandHeader title="サイト埋め込み" />
			<div className="relative max-w-full scroll-pt-10">
				<div className="p-4 pb-20 lg:p-12 laptop:pb-4">
					<div className="space-y-8">
						<SiteEmbedDescription />

						<div className="relative flex gap-x-6">
							<div className="min-w-0 flex-auto space-y-8">
								<SectionCard>
										<QuestionForMain
											title="埋め込み形式"
											inputChildren={
												<div className="space-y-4">
													<div className="grid gap-2 tablet:grid-cols-3">
														{SITE_EMBED_TAB_LIST.map((tab) => {
															const isActive = variant === tab.type;
															return (
																<button
																	key={tab.type}
																	type="button"
																	onClick={() => {
																		setVariant(tab.type);
																	}}
																	className={[
																		"flex items-center gap-2 rounded-md border px-4 py-3 text-left text-sm transition-colors",
																		isActive
																			? "border-brand bg-zinc-50 font-semibold text-black"
																			: "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50",
																	].join(" ")}
																>
																	<span className="shrink-0 text-zinc-500">
																		{VARIANT_ICONS[tab.type]}
																	</span>
																	<p>{tab.label}</p>
																</button>
															);
														})}
													</div>
													<p className="text-sm text-zinc-700">{selectedTab.description}</p>
												</div>
											}
										/>
									</SectionCard>

									<SectionCard>
										<QuestionForMain
											title="オプション設定"
											inputChildren={
												<div className="space-y-4">
													<div className="space-y-2">
														<p className="text-sm font-medium">表示言語</p>
														<div className="max-w-[220px]">
															<Select
																value={lang}
																onValueChange={(value) => {
																	setLang(value as LangType);
																}}
															>
																<SelectTrigger>
																	<SelectValue />
																</SelectTrigger>
																<SelectContent>
																	{LANG_OPTIONS.map((langOption) => (
																		<SelectItem
																			key={langOption.value}
																			value={langOption.value}
																		>
																			{langOption.label}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
														</div>
														<p className="text-xs text-zinc-500">
															「自動」を選ぶと、見ている人のブラウザの言語などに合わせて表示されます。
														</p>
													</div>
												</div>
											}
										/>
									</SectionCard>

									<SectionCard>
										<QuestionForMain
											title="埋め込みタグ"
											belowTitleChildren={
												<p className="text-xs text-zinc-500">
													※ 埋め込み用タグには、この施設を示すID（{hotelID}
													）が含まれています。
												</p>
											}
											inputChildren={
												<div className="space-y-4">
													<div className="space-y-2">
														<div className="flex items-center justify-between gap-2">
															<p className="text-sm font-medium">埋め込み用タグ</p>
															<Button
																type="button"
																variant="outline"
																size="sm"
																onClick={handleCopyScriptSnippet}
															>
																コピー
															</Button>
														</div>
														<pre className="overflow-x-auto rounded-lg border border-zinc-200 bg-zinc-50 p-4 font-mono text-sm leading-relaxed">
															<code>{scriptSnippet}</code>
														</pre>
													</div>

													<div className="space-y-2">
														<div className="flex items-center justify-between gap-2">
															<p className="text-sm font-medium">表示位置のタグ</p>
															<Button
																type="button"
																variant="outline"
																size="sm"
																onClick={handleCopyMountSnippet}
															>
																コピー
															</Button>
														</div>
														<pre className="overflow-x-auto rounded-lg border border-zinc-200 bg-zinc-50 p-4 font-mono text-sm leading-relaxed">
															<code>{mountSnippet}</code>
														</pre>
													</div>
												</div>
											}
										/>
									</SectionCard>

									<div className="overflow-hidden rounded-xl border border-zinc-200 laptop:hidden">
										<SiteEmbedSettingsPreview {...previewProps} layout="mobile" />
									</div>
								</div>

							<div className="sticky top-24 hidden h-[calc(100vh_-_180px)] min-h-0 min-w-[420px] max-w-[520px] shrink-0 self-start border-l border-t border-zinc-200 bg-white px-6 py-5 laptop:flex laptop:flex-col">
								<SiteEmbedSettingsPreview {...previewProps} layout="desktop" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</FeatureIslandContainer>
	);
};
